import { useQuery } from '@tanstack/react-query'
import {
	CheckCircle2,
	Clock,
	type LucideIcon,
	Store,
	TrendingUp,
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { usePageTitle } from '@/hooks/use-page-title'
import { adminApi } from '@/lib/admin-api'
import { cn } from '@/utils/misc'

export function AdminDashboardPage() {
	usePageTitle('Dashboard')

	const statsQuery = useQuery({
		queryKey: ['admin', 'stats'],
		queryFn: adminApi.stats,
	})

	const stats = statsQuery.data

	return (
		<div className="space-y-8">
			<header>
				<h1 className="text-2xl font-bold tracking-tight text-gray-900">
					Dashboard
				</h1>
				<p className="text-sm text-gray-500 mt-1">
					Overview of branches and their connection status
				</p>
			</header>

			<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
				<StatCard
					label="Total branches"
					value={stats?.total}
					icon={Store}
					isLoading={statsQuery.isPending}
					tint="neutral"
				/>
				<StatCard
					label="Connected"
					value={stats?.connected}
					icon={CheckCircle2}
					isLoading={statsQuery.isPending}
					tint="green"
					hint={
						stats && stats.total > 0
							? `${Math.round((stats.connected / stats.total) * 100)}% of total`
							: undefined
					}
				/>
				<StatCard
					label="Pending connection"
					value={stats?.pending}
					icon={Clock}
					isLoading={statsQuery.isPending}
					tint="amber"
					hint={
						stats && stats.total > 0
							? `${Math.round((stats.pending / stats.total) * 100)}% of total`
							: undefined
					}
				/>
				<StatCard
					label="Created last 7 days"
					value={stats?.createdLast7Days}
					icon={TrendingUp}
					isLoading={statsQuery.isPending}
					tint="red"
				/>
			</div>

			{statsQuery.isError && (
				<div className="rounded-xl border border-red-200 bg-red-50 p-4">
					<p className="text-red-700 text-sm">
						Could not load dashboard stats. Try refreshing the page.
					</p>
				</div>
			)}

			{stats && stats.total > 0 && (
				<div className="rounded-xl border border-gray-200 bg-white p-6">
					<h2 className="text-sm font-semibold text-gray-900 mb-4">
						Connection progress
					</h2>
					<div className="flex items-center gap-4">
						<div className="flex-1">
							<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
								<div
									className="h-full bg-green-500 transition-all duration-500"
									style={{
										width: `${(stats.connected / stats.total) * 100}%`,
									}}
								/>
							</div>
						</div>
						<span className="text-sm font-medium text-gray-900 tabular-nums">
							{stats.connected} / {stats.total}
						</span>
					</div>
					<p className="text-xs text-gray-500 mt-3">
						{stats.pending > 0
							? `${stats.pending} branch${stats.pending === 1 ? '' : 'es'} awaiting setup`
							: 'All branches are connected'}
					</p>
				</div>
			)}
		</div>
	)
}

const TINTS = {
	neutral: 'bg-gray-50 text-gray-600',
	green: 'bg-green-50 text-green-600',
	amber: 'bg-amber-50 text-amber-600',
	red: 'bg-red-50 text-red-600',
} as const

function StatCard({
	label,
	value,
	icon: Icon,
	isLoading,
	tint,
	hint,
}: {
	label: string
	value: number | undefined
	icon: LucideIcon
	isLoading: boolean
	tint: keyof typeof TINTS
	hint?: string
}) {
	return (
		<div className="rounded-xl border border-gray-200 bg-white p-5">
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium text-gray-500">{label}</span>
				<div
					className={cn(
						'h-9 w-9 rounded-lg flex items-center justify-center',
						TINTS[tint],
					)}
				>
					<Icon className="h-4 w-4" />
				</div>
			</div>
			<div className="mt-4">
				{isLoading ? (
					<Skeleton className="h-8 w-16" />
				) : (
					<p className="text-3xl font-bold tracking-tight text-gray-900 tabular-nums">
						{value ?? '—'}
					</p>
				)}
				{hint && !isLoading && (
					<p className="text-xs text-gray-500 mt-1">{hint}</p>
				)}
			</div>
		</div>
	)
}
